import { Link } from "wouter";
import { Linkedin, Twitter, Instagram, Facebook, Youtube, Mail, Calendar } from "lucide-react";

export function Footer() {
  return (
    <footer role="contentinfo" aria-label="Site footer" className="bg-card border-t border-white/5 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-6 group">
              <img
                src="/matt-rebeiro-logo.svg"
                alt="Matt Rebeiro Logo"
                className="h-10 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
              />
            </div>
            <p className="text-muted-foreground mb-8 max-w-md">
              The #1 Strategic Advisor for Founders & CEOs. Helping visionary leaders move from strategy through to execution.
            </p>
            <div className="flex gap-4" role="group" aria-label="Social media links">
              <a href="https://www.linkedin.com/in/themattrebeiro/" target="_blank" rel="noreferrer" aria-label="LinkedIn (opens in new tab)" className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
                <Linkedin size={18} aria-hidden="true" />
              </a>
              <a href="https://x.com/themattrebeiro" target="_blank" rel="noreferrer" aria-label="Twitter/X (opens in new tab)" className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
                <Twitter size={18} aria-hidden="true" />
              </a>
              <a href="https://www.instagram.com/themattrebeiro/" target="_blank" rel="noreferrer" aria-label="Instagram (opens in new tab)" className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
                <Instagram size={18} aria-hidden="true" />
              </a>
              <a href="https://www.facebook.com/themattrebeiro/" target="_blank" rel="noreferrer" aria-label="Facebook (opens in new tab)" className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
                <Facebook size={18} aria-hidden="true" />
              </a>
              <a href="https://www.youtube.com/@themattrebeiro" target="_blank" rel="noreferrer" aria-label="YouTube (opens in new tab)" className="w-11 h-11 rounded-full bg-accent flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-all">
                <Youtube size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
          
          <nav aria-label="Contact information">
            <p className="font-display font-semibold text-lg mb-6">Contact</p>
            <ul className="space-y-4">
              <li>
                <a href="mailto:advisor@mattrebeiro.com" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <Mail size={16} aria-hidden="true" /> Advisor Inquiries
                </a>
              </li>
              <li>
                <a href="mailto:speaking@mattrebeiro.com" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <Mail size={16} aria-hidden="true" /> Speaking Events
                </a>
              </li>
              <li>
                <a href="mailto:events@mattrebeiro.com" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <Calendar size={16} aria-hidden="true" /> Event Inquiries
                </a>
              </li>
              <li>
                <a href="mailto:podcast@mattrebeiro.com" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <Mail size={16} aria-hidden="true" /> Podcast Guests
                </a>
              </li>
              <li>
                <a href="mailto:sponsorship@mattrebeiro.com" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                  <Mail size={16} aria-hidden="true" /> Sponsorships
                </a>
              </li>
            </ul>
          </nav>

          <nav aria-label="Quick links">
            <p className="font-display font-semibold text-lg mb-6">Links</p>
            <ul className="space-y-4">
              <li>
                <a href="https://techretriever.io/subscribe" target="_blank" rel="noreferrer" aria-label="The Tech Retriever (opens in new tab)" className="text-muted-foreground hover:text-primary transition-colors">
                  The Tech Retriever
                </a>
              </li>
              <li>
                <Link href="/podcast" className="text-muted-foreground hover:text-primary transition-colors">
                  The Startup Bootstrapper
                </Link>
              </li>
              <li>
                <Link href="/book" className="text-muted-foreground hover:text-primary transition-colors">
                  Pre-order my book
                </Link>
              </li>
              <li>
                <a href="https://www.3pivots.com" target="_blank" rel="noreferrer" aria-label="3 Pivots (opens in new tab)" className="text-muted-foreground hover:text-primary transition-colors">
                  3 Pivots
                </a>
              </li>
            </ul>
          </nav>
        </div>
        
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Process Design Institute Ltd. All rights reserved.</p>
          <nav className="flex gap-4 mt-4 md:mt-0" aria-label="Legal links">
            <Link href="/terms" className="hover:text-foreground transition-colors" data-testid="link-terms">Terms of Use</Link>
            <Link href="/privacy" className="hover:text-foreground transition-colors" data-testid="link-privacy">Privacy Policy</Link>
            <Link href="/disclaimer" className="hover:text-foreground transition-colors" data-testid="link-disclaimer">Disclaimer</Link>
            <Link href="/accessibility" className="hover:text-foreground transition-colors" data-testid="link-accessibility">Accessibility</Link>
          </nav>
        </div>

        <div className="border-t border-white/5 mt-8 pt-6">
          <p className="text-xs text-muted-foreground text-center max-w-3xl mx-auto leading-relaxed">
            This website is operated by Process Design Institute Ltd. Process Design Institute and its brands Matt Rebeiro, 3 Pivots, and Tech Retriever are independent publishers. References to companies, products, and services are for informational and editorial purposes only. All trademarks, service marks, logos, and brand names mentioned on this site, including registered trademarks, are the property of their respective owners. Their use does not imply affiliation, partnership, or endorsement.
          </p>
        </div>
      </div>
    </footer>
  );
}
